// sanitize.js — the least-data rules from the analytics contract (§4, §7).
//
// Everything here is pure and synchronous so it can run inside PostHog's
// `before_send` hook and inside the signup-link builder without touching the
// network or storage. Nothing in this file ever *repairs* a bad value: a value
// that fails validation is dropped, because a "fixed" value would be a guess.

// ── §4 attribution rules ────────────────────────────────────────────────────

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Click ids travel as presence booleans only. The values are never stored.
export const CLICK_ID_KEYS = ['gclid', 'twclid', 'fbclid'];

// Every click-id style parameter PostHog's own campaign capture knows about
// (posthog-js 1.390.2 CAMPAIGN_PARAMS), plus the non-allowlisted utm_* and
// Google params. None of these values may leave the browser.
const CLICK_ID_LIKE = new Set([
  'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'twclid',
  'li_fat_id', 'igshid', 'ttclid', 'rdt_cid', 'epik', 'qclid', 'sccid', 'irclid',
  '_kx', 'gad_source', 'mc_cid',
]);

const ATTR_VALUE_RE = /^[a-z0-9._~:+-]{1,100}$/;

/** URL-decode, trim, lowercase, spaces → `_`, then validate. Invalid → null. */
export function normalizeAttributionValue(raw) {
  if (raw == null) return null;
  let value = String(raw);
  try {
    value = decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
  value = value.trim().toLowerCase().replace(/\s+/g, '_');
  return ATTR_VALUE_RE.test(value) ? value : null;
}

const CV_REF_SRC_RE = /^[a-z0-9_]{1,64}$/;
const CV_REF_SURFACES = new Set(['website', 'explorer']);
const CV_REF_CAMPAIGNS = new Set(['developer_signup', 'pulse_explorer']);

export function isValidCtaId(value) {
  return typeof value === 'string' && CV_REF_SRC_RE.test(value);
}

/** Coerce a CTA id into the contract grammar `^[a-z0-9_]{1,64}$`, or null. */
export function normalizeCtaId(raw) {
  if (raw == null) return null;
  const value = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return isValidCtaId(value) ? value : null;
}

function validCvRef(key, value) {
  if (key === 'cv_ref_src') return isValidCtaId(value) ? value : null;
  if (key === 'cv_ref_surface') return CV_REF_SURFACES.has(value) ? value : null;
  if (key === 'cv_ref_campaign') return CV_REF_CAMPAIGNS.has(value) ? value : null;
  return null;
}

const HOST_RE = /^[a-z0-9.-]{1,100}$/;
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

export function isCoinversaHost(host) {
  return /(^|\.)coinversa\.ai$/i.test(host || '');
}

/**
 * `referrer_host` per §4: lowercased hostname only (no port/path/query);
 * Coinversa hosts → `internal`; empty → `direct`. IP literals and anything
 * that is not a plain hostname are dropped (null).
 */
export function referrerHost(referrer) {
  if (!referrer) return 'direct';
  let host;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!host) return 'direct';
  if (isCoinversaHost(host)) return 'internal';
  if (IPV4_RE.test(host) || !HOST_RE.test(host)) return null;
  return host;
}

// ── §7 path redaction ───────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_SEGMENT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LONG_HEX_RE = /^(0x)?[0-9a-f]{48,}$/i;
const DIGIT_RUN_RE = /\d{10,}/;

function redactSegment(segment) {
  if (!segment) return segment;
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    /* keep raw */
  }
  if (
    UUID_RE.test(decoded) ||
    /^did:/i.test(decoded) ||
    /^(cvsa|mch)_/i.test(decoded) ||
    EMAIL_SEGMENT_RE.test(decoded) ||
    LONG_HEX_RE.test(decoded) ||
    DIGIT_RUN_RE.test(decoded)
  ) {
    return ':id';
  }
  return segment;
}

/** Replace identifying path segments with `:id`. 0x addresses, coins and dates survive. */
export function redactPath(pathname) {
  if (typeof pathname !== 'string') return pathname;
  return pathname.split('/').map(redactSegment).join('/');
}

// ── §7 value redaction ──────────────────────────────────────────────────────

const VALUE_PATTERNS = [
  /cvsa_[A-Za-z0-9_-]{6,}/g,
  /mch_[A-Za-z0-9_-]{6,}/g,
  /did:privy:[A-Za-z0-9]+/g,
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g,
  /[^\s@"'<>]+@[^\s@"'<>]+\.[^\s@"'<>]+/g,
  /\b[0-9a-f]{64}\b/gi,
  /Bearer\s+\S+/g,
];

export function redactString(value) {
  if (typeof value !== 'string') return value;
  let out = value;
  for (const re of VALUE_PATTERNS) out = out.replace(re, '[redacted]');
  return out;
}

// ── §7 URL reduction ────────────────────────────────────────────────────────

const MAX_URL = 512;
const MAX_STRING = 200;

function baseOrigin() {
  try {
    return window.location.origin;
  } catch {
    return 'https://www.coinversa.ai';
  }
}

/**
 * origin + redacted path + only sanitized `utm_*` and `cv_ref_*` params.
 * Fragments, every other param and credentials in the authority are dropped.
 * Relative URLs resolve against the current origin but stay relative.
 * Unparseable → undefined (the property is removed).
 */
export function sanitizeUrl(raw) {
  if (typeof raw !== 'string' || !raw) return undefined;
  const trimmed = raw.trim();
  if (/^(mailto|tel):/i.test(trimmed)) return trimmed.split('?')[0].replace(/:.*/, ':[redacted]');
  if (/^(javascript|data|blob):/i.test(trimmed)) return undefined;
  let url;
  const relative = !/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !trimmed.startsWith('//');
  try {
    url = new URL(trimmed, baseOrigin());
  } catch {
    return undefined;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    const k = key.toLowerCase();
    if (UTM_KEYS.includes(k)) {
      const v = normalizeAttributionValue(value);
      if (v) kept.set(k, v);
    } else if (k.startsWith('cv_ref_')) {
      const v = validCvRef(k, value);
      if (v) kept.set(k, v);
    }
  });
  const query = kept.toString();
  const path = redactPath(url.pathname);
  const out = `${relative ? '' : url.origin}${path}${query ? `?${query}` : ''}`;
  return out.slice(0, MAX_URL);
}

export function originOnly(raw) {
  if (typeof raw !== 'string' || !raw) return raw === '' ? '' : undefined;
  if (raw === '$direct') return raw; // PostHog's marker for "no referrer"
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    return url.origin;
  } catch {
    return undefined;
  }
}

// ── §7 property scrubbing ───────────────────────────────────────────────────

const DROP_KEYS = new Set([
  'api_key', 'apikey', 'key', 'x-api-key', 'token', 'access_token', 'refresh_token',
  'id_token', 'code', 'state', 'phid', 'password', 'secret', 'signature',
  'authorization', 'email', '$email', 'key_hash', 'keyhash', 'key_prefix',
  'keyprefix', 'raw_key', 'ip', '$ip', 'search', 'full_endpoint', 'query',
  // posthog-js super property: the visitor's raw search-engine query, read
  // from the referrer's q/p param (§4: the raw search string is never sent).
  // $search_engine alone is kept.
  'ph_keyword',
]);

// posthog-js drops any event whose top-level `properties.token` (the public
// project ingest key) is missing after before_send ("required for ingestion"),
// so that one SDK-managed key is exempt from the drop list. `distinct_id` is
// exempt from value redaction by contract.
const TOP_LEVEL_EXEMPT = new Set(['token', 'distinct_id']);

const URL_KEY_RE = /(^|[_$])(url|href)$/i;
const PATH_KEY_RE = /(^|_)path$/i;
const REFERRER_KEY_RE = /referrer$/i;
const PATHNAME_KEY_RE = /pathname$/i;
const UTM_PROP_RE = /(^|_)utm_([a-z_]+)$/i;

function campaignKeyBase(key) {
  // $initial_gclid, $session_entry_fbclid, gclid → gclid
  return key.replace(/^\$(initial|session_entry)_/i, '').toLowerCase();
}

function sanitizeElementsChain(chain) {
  const withHrefs = chain.replace(
    /(attr__href|href)="((?:[^"\\]|\\.)*)"/g,
    (_, attr, href) => `${attr}="${sanitizeUrl(href.replace(/\\"/g, '"')) || ''}"`,
  );
  return redactString(withHrefs);
}

// posthog-js heatmaps key their buffer by the raw `window.location.href`
// (heatmaps.js `_capture`), so the URL is an object KEY that value rules never
// see. Heatmaps can be switched on from the PostHog project settings without a
// code change. Re-key by the reduced URL (merging buckets that collapse into
// one) and drop any bucket whose URL cannot be reduced.
function sanitizeHeatmapData(data, depth, custom) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined;
  const out = {};
  for (const rawUrl of Object.keys(data)) {
    const url = sanitizeUrl(rawUrl);
    if (!url) continue;
    const items = Array.isArray(data[rawUrl]) ? data[rawUrl] : [];
    // eslint-disable-next-line no-use-before-define
    const clean = items.map((item) => sanitizeValue('', item, depth + 1, custom));
    out[url] = (out[url] || []).concat(clean);
  }
  return out;
}

function sanitizeValue(key, value, depth, custom) {
  if (value == null || typeof value === 'boolean' || typeof value === 'number') return value;

  if (key === '$heatmap_data') return sanitizeHeatmapData(value, depth, custom);

  if (typeof value === 'string') {
    if (key === '$elements_chain') return sanitizeElementsChain(value);
    if (URL_KEY_RE.test(key)) return sanitizeUrl(value);
    if (REFERRER_KEY_RE.test(key)) return originOnly(value);
    if (PATHNAME_KEY_RE.test(key) || PATH_KEY_RE.test(key)) {
      return redactString(redactPath(value.split(/[?#]/)[0])).slice(0, custom ? MAX_STRING : MAX_URL);
    }
    const utm = key.match(UTM_PROP_RE);
    if (utm) {
      if (!UTM_KEYS.includes(`utm_${utm[2].toLowerCase()}`)) return undefined;
      return normalizeAttributionValue(value) || undefined;
    }
    const redacted = redactString(value);
    return custom ? redacted.slice(0, MAX_STRING) : redacted;
  }

  if (Array.isArray(value)) {
    if (custom && depth >= 3) return undefined;
    const list = custom ? value.slice(0, 20) : value;
    return list.map((item) => sanitizeValue('', item, depth + 1, custom));
  }

  if (typeof value === 'object') {
    if (custom && depth >= 3) return undefined;
    // eslint-disable-next-line no-use-before-define
    return sanitizeObject(value, depth + 1, custom);
  }

  return undefined; // functions, symbols, bigint
}

function sanitizeObject(obj, depth = 0, parentCustom = false, topLevel = false) {
  const out = {};
  for (const key of Object.keys(obj)) {
    const lower = key.toLowerCase();
    const value = obj[key];
    if (topLevel && TOP_LEVEL_EXEMPT.has(lower)) {
      out[key] = value;
      continue;
    }
    if (DROP_KEYS.has(lower)) continue;
    if (CLICK_ID_LIKE.has(campaignKeyBase(key))) continue;
    // $set / $set_once nested in properties are person properties: same rules.
    const custom = parentCustom || !key.startsWith('$');
    const clean = sanitizeValue(key, value, depth, custom);
    if (clean !== undefined) out[key] = clean;
  }
  return out;
}

/** Sanitize one properties bag (event properties, $set or $set_once). */
export function sanitizeProperties(props, { topLevel = true } = {}) {
  if (!props || typeof props !== 'object') return props;
  return sanitizeObject(props, 0, false, topLevel);
}

/**
 * PostHog `before_send`. Applies §7 to properties, $set and $set_once, and
 * fails closed: if scrubbing throws, the event is dropped (null).
 */
export function beforeSend(event) {
  try {
    if (!event || typeof event !== 'object') return event;
    const next = { ...event };
    if (event.properties) next.properties = sanitizeProperties(event.properties);
    if (event.$set) next.$set = sanitizeProperties(event.$set, { topLevel: false });
    if (event.$set_once) next.$set_once = sanitizeProperties(event.$set_once, { topLevel: false });
    return next;
  } catch {
    return null;
  }
}
